export const PEP_DESKTOP_APP_VERSION_FILTER_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'needsUpdate', label: 'Needs update' },
  { id: 'latest', label: 'On latest' },
  { id: 'mixed', label: 'Mixed' },
  { id: 'noApp', label: 'No app' }
]

export const PEP_DESKTOP_APP_VERSION_STATES = {
  Latest: 'Latest',
  Outdated: 'Outdated',
  Mixed: 'Mixed',
  NoApp: 'NoApp',
  Unknown: 'Unknown'
}

export function parsePepDesktopAppVersionsCsv(versionsCsv) {
  if (versionsCsv == null || String(versionsCsv).trim() === '') {
    return []
  }

  return String(versionsCsv)
    .split(',')
    .map(v => v.trim())
    .filter(Boolean)
}

export function compareSemverVersions(left, right) {
  const leftParts = parseSemverParts(left)
  const rightParts = parseSemverParts(right)

  for (let i = 0; i < 3; i += 1) {
    if (leftParts[i] > rightParts[i]) {
      return 1
    }
    if (leftParts[i] < rightParts[i]) {
      return -1
    }
  }

  return 0
}

function parseSemverParts(version) {
  if (version == null || String(version).trim() === '') {
    return [0, 0, 0]
  }

  const parts = String(version)
    .trim()
    .split('.')
    .map(part => Number.parseInt(part, 10))

  return [
    Number.isNaN(parts[0]) ? 0 : parts[0],
    Number.isNaN(parts[1]) ? 0 : parts[1],
    Number.isNaN(parts[2]) ? 0 : parts[2]
  ]
}

export function getPepDesktopAppDeviceCount(hotel) {
  const count = hotel?.pepDesktopAppDeviceCount ?? hotel?.PepDesktopAppDeviceCount
  const parsed = Number.parseInt(count, 10)
  return Number.isNaN(parsed) ? 0 : parsed
}

export function getPepDesktopAppVersionsCsv(hotel) {
  return hotel?.pepDesktopAppVersions ?? hotel?.PepDesktopAppVersions ?? ''
}

export function derivePepDesktopAppVersionState(hotel, latestRelease) {
  const deviceCount = getPepDesktopAppDeviceCount(hotel)
  const versions = parsePepDesktopAppVersionsCsv(getPepDesktopAppVersionsCsv(hotel))

  if (deviceCount <= 0) {
    return PEP_DESKTOP_APP_VERSION_STATES.NoApp
  }

  if (versions.length === 0) {
    return PEP_DESKTOP_APP_VERSION_STATES.Unknown
  }

  if (versions.length > 1) {
    return PEP_DESKTOP_APP_VERSION_STATES.Mixed
  }

  if (!latestRelease) {
    return PEP_DESKTOP_APP_VERSION_STATES.Unknown
  }

  return compareSemverVersions(versions[0], latestRelease) >= 0
    ? PEP_DESKTOP_APP_VERSION_STATES.Latest
    : PEP_DESKTOP_APP_VERSION_STATES.Outdated
}

export function matchesPepDesktopAppVersionFilter(state, filterId) {
  if (!filterId || filterId === 'all') {
    return true
  }

  if (filterId === 'needsUpdate') {
    return (
      state === PEP_DESKTOP_APP_VERSION_STATES.Outdated ||
      state === PEP_DESKTOP_APP_VERSION_STATES.Mixed
    )
  }

  if (filterId === 'latest') {
    return state === PEP_DESKTOP_APP_VERSION_STATES.Latest
  }

  if (filterId === 'mixed') {
    return state === PEP_DESKTOP_APP_VERSION_STATES.Mixed
  }

  if (filterId === 'noApp') {
    return (
      state === PEP_DESKTOP_APP_VERSION_STATES.NoApp ||
      state === PEP_DESKTOP_APP_VERSION_STATES.Unknown
    )
  }

  return true
}

export function getPepDesktopAppVersionDisplay(hotel, latestRelease) {
  const state = derivePepDesktopAppVersionState(hotel, latestRelease)
  const versions = parsePepDesktopAppVersionsCsv(getPepDesktopAppVersionsCsv(hotel))
  const deviceCount = getPepDesktopAppDeviceCount(hotel)

  if (state === PEP_DESKTOP_APP_VERSION_STATES.NoApp) {
    return {
      state,
      label: 'No app',
      versionText: '—',
      title: 'No registered PEP desktop device for this hotel'
    }
  }

  if (state === PEP_DESKTOP_APP_VERSION_STATES.Unknown) {
    return {
      state,
      label: 'Unknown',
      versionText: deviceCount > 0 ? `${deviceCount} device${deviceCount === 1 ? '' : 's'}` : '—',
      title: 'Device registered but app version was not reported'
    }
  }

  if (state === PEP_DESKTOP_APP_VERSION_STATES.Mixed) {
    return {
      state,
      label: 'Mixed',
      versionText: versions.join(', '),
      title: `Multiple desktop app versions on ${deviceCount} device${deviceCount === 1 ? '' : 's'}`
    }
  }

  const versionText = versions[0] ?? '—'
  const label =
    state === PEP_DESKTOP_APP_VERSION_STATES.Latest ? 'On latest' : 'Needs update'

  return {
    state,
    label,
    versionText,
    title:
      state === PEP_DESKTOP_APP_VERSION_STATES.Latest
        ? `Desktop app ${versionText} matches release ${latestRelease}`
        : `Desktop app ${versionText}; latest release is ${latestRelease}`
  }
}

export function buildPmsDesktopAppVersionSummary(hotels, latestRelease) {
  const summary = {
    total: hotels.length,
    latest: 0,
    needsUpdate: 0,
    mixed: 0,
    noApp: 0
  }

  hotels.forEach(hotel => {
    const state = derivePepDesktopAppVersionState(hotel, latestRelease)

    if (state === PEP_DESKTOP_APP_VERSION_STATES.Latest) {
      summary.latest += 1
      return
    }

    if (
      state === PEP_DESKTOP_APP_VERSION_STATES.Outdated ||
      state === PEP_DESKTOP_APP_VERSION_STATES.Mixed
    ) {
      summary.needsUpdate += 1
    }

    if (state === PEP_DESKTOP_APP_VERSION_STATES.Mixed) {
      summary.mixed += 1
      return
    }

    if (
      state === PEP_DESKTOP_APP_VERSION_STATES.NoApp ||
      state === PEP_DESKTOP_APP_VERSION_STATES.Unknown
    ) {
      summary.noApp += 1
    }
  })

  return summary
}

export const DESKTOP_APP_RELEASES_URL =
  'https://directful-desktop-app.s3.us-west-2.amazonaws.com/updates/squirrel.windows/ia32/RELEASES'

export function parseLatestDesktopAppRelease(content) {
  let latestVersion = '0.0.0'

  String(content || '')
    .split('\n')
    .forEach(line => {
      const parts = line.split(' ')
      if (parts.length < 2) {
        return
      }

      const filename = parts[1]
      const versionMatch = filename.match(/directful-desktop-app-(\d+\.\d+\.\d+)/i)
      if (!versionMatch || !versionMatch[1]) {
        return
      }

      const version = versionMatch[1]
      if (compareSemverVersions(version, latestVersion) > 0) {
        latestVersion = version
      }
    })

  return latestVersion === '0.0.0' ? null : latestVersion
}
